import { useSelector } from 'react-redux';
import { CompanyPlanStatus } from '../enum/CompanyPlanLevelEnum';
import { isOperationalCountryMexico } from './company/fiscalSettings';
import { canShowFiscalFeature, FiscalFeature } from './fiscal/fiscalFeatureVisibility';
import useInvoiceModuleCapabilities from './invoiceModule/useInvoiceModuleCapabilities';

// Operational CFDI access hook for existing CFDI flows.
// Keep create/view/mutate CFDI actions behind this hook because it also enforces
// plan-status and user-permission checks beyond simple module visibility.
const useCanUseCFDIInvoicing = (
  _storeId: string
): {
  canUseCFDIInvoicing: boolean;
  canViewCFDI: boolean;
  canMutateCFDI: boolean;
} => {
  const { company, user } = useSelector((state: any) => state.user);
  const invoiceModuleCapabilities = useInvoiceModuleCapabilities();
  const hasLoadedCapabilities =
    invoiceModuleCapabilities.capabilities !== null &&
    !invoiceModuleCapabilities.isLoading &&
    !invoiceModuleCapabilities.isError;
  const canViewCFDI = canShowFiscalFeature(FiscalFeature.MEXICO_CFDI_OPERATIONAL, {
    company,
    user
  });
  const isInGraceOrSuspended = [CompanyPlanStatus.GRACE, CompanyPlanStatus.SUSPENDED].includes(
    company?.planStatus
  );
  const hasCFDIServiceEnabled = company?.companyServicesPermissions?.canUseCFDIInvoicing === true;
  // During rollout, legacy users may still miss permission.cfdi until migration completes.
  // Fallback to true to preserve prior behavior temporarily.
  const canMutateByUserPermission = user?.permission?.cfdi?.canMutate ?? true;
  const canMutateCFDI =
    isOperationalCountryMexico(company) &&
    hasCFDIServiceEnabled &&
    !isInGraceOrSuspended &&
    canMutateByUserPermission;

  if (hasLoadedCapabilities) {
    return {
      canUseCFDIInvoicing: invoiceModuleCapabilities.canViewCFDI,
      canViewCFDI: invoiceModuleCapabilities.canViewCFDI,
      canMutateCFDI: invoiceModuleCapabilities.canMutateCFDI
    };
  }

  return {
    canUseCFDIInvoicing: canViewCFDI,
    canViewCFDI,
    canMutateCFDI
  };
};

export default useCanUseCFDIInvoicing;
